import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Gauge, TriangleAlert, Radio } from "lucide-react";
import { useCountUp } from "@/components/broadcast/shared";

export function useThreatIndex() {
  const [data, setData] = useState(null);
  useEffect(() => {
    const load = () => api.get("/threat-index").then((r) => setData(r.data)).catch(() => {});
    load();
    const t = setInterval(load, 180_000);
    return () => clearInterval(t);
  }, []);
  return data;
}

// Tier bands (score floor → color) for the gauge arc.
const BANDS = [
  { from: 0, to: 10, color: "#1f63c9" },
  { from: 10, to: 30, color: "#2a9d3a" },
  { from: 30, to: 50, color: "#eab308" },
  { from: 50, to: 70, color: "#f6a21c" },
  { from: 70, to: 90, color: "#d90429" },
  { from: 90, to: 100, color: "#7a0416" },
];

const CX = 200, CY = 205, R = 165;
const polar = (deg) => {
  const rad = (deg * Math.PI) / 180;
  return { x: CX + R * Math.cos(rad), y: CY - R * Math.sin(rad) };
};
const angleFor = (score) => 180 - (Math.max(0, Math.min(100, score)) / 100) * 180;
const arc = (s1, s2) => {
  const p1 = polar(angleFor(s1)), p2 = polar(angleFor(s2));
  return `M ${p1.x.toFixed(1)} ${p1.y.toFixed(1)} A ${R} ${R} 0 0 1 ${p2.x.toFixed(1)} ${p2.y.toFixed(1)}`;
};

export function ThreatIndexSlide({ threat }) {
  const raw = useCountUp(threat?.score ?? null, { duration: 1100 });
  if (!threat || threat.score == null) return null;
  const { score, level, color } = threat;
  const displayScore = Math.round(raw == null ? score : raw);
  const needle = polar(angleFor(displayScore));
  const drivers = threat.drivers || [];
  return (
    <div data-testid="threat-index-slide" className="relative h-full w-full overflow-hidden" style={{ background: "radial-gradient(1200px 700px at 30% -10%, #16233f 0%, #0a0f1c 55%, #080c16 100%)" }}>
      <div className="absolute inset-0 opacity-[0.06]" style={{ backgroundImage: "repeating-linear-gradient(135deg, #fff 0 2px, transparent 2px 26px)" }} />
      <div className="relative grid h-full w-full grid-cols-[46%_1fr] items-center gap-6 px-[56px] py-[26px]">
        {/* Gauge */}
        <div className="flex flex-col items-center">
          <div className="flex items-center gap-2.5 font-oswald text-[18px] font-bold uppercase tracking-[0.26em] text-[#9fb2d4]">
            <Gauge className="h-6 w-6" strokeWidth={2.5} /> GAWX Storm Threat Index
          </div>
          <svg viewBox="0 0 400 235" className="mt-1 w-full max-w-[560px]">
            <path d={arc(0, 100)} fill="none" stroke="#1b2740" strokeWidth="26" strokeLinecap="round" />
            {BANDS.map((b) => <path key={b.from} d={arc(b.from, b.to)} fill="none" stroke={b.color} strokeWidth="26" opacity={displayScore >= b.from ? 1 : 0.22} />)}
            <line x1={CX} y1={CY} x2={needle.x.toFixed(1)} y2={needle.y.toFixed(1)} stroke="#fff" strokeWidth="6" strokeLinecap="round" />
            <circle cx={CX} cy={CY} r="13" fill="#fff" />
            <circle cx={CX} cy={CY} r="6" fill={color} />
            <text x={CX} y="150" textAnchor="middle" fontFamily="Oswald, sans-serif" fontWeight="900" fontSize="96" fill="#fff">{displayScore}</text>
            <text x={CX} y="192" textAnchor="middle" fontFamily="Oswald, sans-serif" fontWeight="800" fontSize="30" letterSpacing="3" fill={color} style={{ textTransform: "uppercase" }}>{level}</text>
          </svg>
          <div className="-mt-2 flex w-full max-w-[560px] justify-between px-6 font-oswald text-[18px] font-bold uppercase tracking-wider text-[#5b6675]">
            <span>Quiet</span><span>Extreme</span>
          </div>
        </div>

        {/* Drivers */}
        <div className="flex flex-col gap-3 rounded-[10px] bg-white/[0.04] p-6 ring-1 ring-white/10">
          <div className="font-oswald text-[25px] font-black uppercase tracking-wider text-white">What's Driving It</div>
          {drivers.length ? drivers.map((d, i) => (
            <div key={i} className="flex items-center justify-between gap-4 border-b border-white/10 pb-2.5" data-testid={`threat-driver-${i}`}>
              <span className="font-oswald text-[24px] font-bold uppercase text-[#e7edf6]">{d.text}</span>
              <span className="rounded-[4px] bg-white/10 px-3 py-0.5 font-oswald text-[20px] font-black tabular-nums text-[#ffd60a]">+{d.weight}</span>
            </div>
          )) : (
            <div className="font-oswald text-[22px] font-semibold uppercase text-[#7e8aa0]">No active severe drivers — quiet pattern.</div>
          )}
          <div className="mt-2 flex flex-wrap items-center gap-2.5">
            {threat.active_watch && (
              <span className="flex items-center gap-2 rounded-[4px] bg-[#f6a21c] px-3.5 py-1.5 font-oswald text-[18px] font-black uppercase tracking-wide text-[#0a0f1c]">
                <TriangleAlert className="h-5 w-5" /> {threat.active_watch === "tornado" ? "Tornado Watch" : "Severe Watch"}
              </span>
            )}
            {threat.active_warnings > 0 && (
              <span className="flex items-center gap-2 rounded-[4px] bg-[#d90429] px-3.5 py-1.5 font-oswald text-[18px] font-black uppercase tracking-wide text-white">
                <Radio className="h-5 w-5" /> {threat.active_warnings} Active Warning{threat.active_warnings > 1 ? "s" : ""}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
